import React from 'react';
import { sub, getUnixTime, format } from 'date-fns';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

import DoneIcon from 'common/assets/icons/success-failure-warn/success/done.svg';
import Tag from 'Components/Tag/Tag';
import {
  renderWithoutZero,
  renderWithoutZeroAndRound,
} from 'common/utils/table.util';
import BuyInIcon from '../../common/assets/icons/common/buy-sell/in-buy.svg';
import BuyOutIcon from '../../common/assets/icons/common/buy-sell/out-buy.svg';
import SellInIcon from '../../common/assets/icons/common/buy-sell/in-sell.svg';
import SellOutIcon from '../../common/assets/icons/common/buy-sell/out-sell.svg';

export const mapDealToIcon = ({ type, operation: { is_entry } }) => {
  if (type === 'buy') {
    return is_entry ? BuyInIcon : BuyOutIcon;
  }
  if (type === 'sell') {
    return is_entry ? SellInIcon : SellOutIcon;
  }
  if (type === 'balance') {
    return DoneIcon;
  }
  return null;
};

export const handleTimeRangeOption = (option) => {
  const date = (number, type) => {
    const date = new Date();
    const subDate = sub(date, {
      ...(type === 'days' && { days: number }),
      ...(type === 'weeks' && { weeks: number }),
      ...(type === 'months' && { months: number }),
    });

    return {
      dateStart: getUnixTime(subDate) * 1000,
      dateEnd: +date,
    };
  };

  switch (option) {
    case '1D':
      return date(1, 'days');
    case '3D':
      return date(3, 'days');
    case '1W':
      return date(1, 'weeks');
    case '1M':
      return date(1, 'months');
    case '3M':
      return date(3, 'months');
    default:
      return { dateStart: null, dateEnd: null };
  }
};

export const handleOperationTypeText = (type, profit) => {
  if (type === 'buy') return 'Buy';
  if (type === 'sell') return 'Sell';
  if (type === 'balance') {
    if (profit > 0) return 'Deposit';
    return 'Withdrawal';
  }
  if (type === 'bonus') return 'Bonus';
  return type;
};

export const TimeRangeOptions = [
  { label: 'All', value: null },
  { label: '1 day', value: '1D' },
  { label: '3 days', value: '3D' },
  { label: '1 week', value: '1W' },
  { label: '1 month', value: '1M' },
  { label: '3 months', value: '3M' },
  { label: 'Custom', value: 'CUSTOM' },
];

export const OperationKindsOptions = [
  { label: 'All', value: null },
  { label: 'Trades', value: 'trade' },
  { label: 'Deposit', value: 'deposit' },
  { label: 'Withdraw', value: 'withdraw' },
  { label: 'Bonus', value: 'bonus' },
  { label: 'Balance compensation', value: 'negative_balance_compensation' },
];

const Text = styled.div`
  font-weight: normal;
  font-size: 14px;
  line-height: 140%;
  color: #303742;
`;

const SubText = styled.div`
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  color: #7e8eaa;
`;

const BuySellType = styled.span`
  display: block;
  min-width: 88px;
  color: ${ifProp('isBuy', '#19AE53', '#FF0000')};
`;

const CenterAll = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) =>
    props.inherit
      ? css`
          left: 5px;
          position: relative;
          top: -6px;
        `
      : ''}
  ${(props) =>
    props.icon
      ? css`
          height: 68px;
          width: 30px;
          background: #fbfbfb;
          padding: 0;
        `
      : ''}
`;

const CenterVertical = styled.div`
  display: flex;
  align-items: center;
`;

export const getAccountOperationColumns = (isTablet) => [
  {
    Header: '',
    dataIndex: 'operationType',
    accessor: (row) => row,
    id: 'operationType',
    noHorizontalPadding: true,
    Cell: ({
      cell: {
        row: { original },
      },
    }) => (
      <CenterAll icon data-testid="accounts-history-body-operation-type">
        <img
          src={mapDealToIcon(original)}
          width={21}
          height={21}
          alt="type"
          draggable={false}
        />
      </CenterAll>
    ),
    maxWidth: 14,
    width: 14,
    hide: () => isTablet,
  },
  {
    Header: 'Time',
    dataIndex: 'time',
    accessor: 'time',
    dataTestID: 'accounts-history-header-time',
    dataTestIDBody: 'accounts-history-body-time',
    Cell: ({
      cell: {
        row: { original },
      },
    }) => <Text accent>{format(original.time, 'yyyy.MM.dd HH:mm:ss')}</Text>,
  },
  {
    Header: 'Ticket',
    dataIndex: 'trade_id',
    accessor: 'trade_id',
    align: 'left',
    dataTestID: 'accounts-history-header-trade-id',
    dataTestIDBody: 'accounts-history-body-trade-id',
  },
  {
    Header: 'Type',
    dataIndex: 'type',
    accessor: 'type',
    dataTestID: 'accounts-history-header-type',
    dataTestIDBody: 'accounts-history-body-type',
    Cell: ({
      cell: {
        row: { original },
      },
      value,
    }) => (
      <CenterVertical>
        {isTablet && (
          <img
            style={{ marginRight: 8 }}
            src={mapDealToIcon(original)}
            alt="icon"
          />
        )}
        <Text
          underline={value === 'sell' || value === 'buy'}
          type={value}
          transform="capitalize"
        >
          {['sell', 'buy'].includes(original.type) ? (
            <>
              <div>
                Trade
                {original.operation.is_entry ? 'Opened' : 'Closed'}
              </div>
              <BuySellType isBuy={original.type === 'buy'}>
                {handleOperationTypeText(original.type, original.profit)}
              </BuySellType>
            </>
          ) : (
            <>
              {handleOperationTypeText(original.type, original.profit)}
              <SubText>{original.comment}</SubText>
            </>
          )}
        </Text>
      </CenterVertical>
    ),
  },
  {
    Header: 'Symbol',
    dataIndex: 'symbol',
    accessor: 'symbol',
    dataTestID: 'accounts-history-header-symbol',
    dataTestIDBody: 'accounts-history-body-symbol',
    Cell: ({ cell: { value } }) =>
      value ? (
        <Tag underline type={value} transform="capitalize">
          {value}
        </Tag>
      ) : null,
  },
  {
    Header: 'Volume',
    dataIndex: 'volume',
    accessor: 'volume',
    align: 'left',
    dataTestID: 'accounts-history-header-volume',
    dataTestIDBody: 'accounts-history-body-volume',
    Cell: renderWithoutZero,
  },
  {
    Header: 'Price',
    dataIndex: 'price',
    accessor: (row) => row.operation.price,
    align: 'right',
    dataTestID: 'accounts-history-header-price',
    dataTestIDBody: 'accounts-history-body-price',
    Cell: renderWithoutZero,
  },
  {
    Header: 'S/L',
    dataIndex: 'sl',
    accessor: (row) => row.operation.sl,
    dataTestID: 'accounts-history-header-operation-sl',
    dataTestIDBody: 'accounts-history-body-operation-sl',
    Cell: renderWithoutZero,
  },
  {
    Header: 'T/P',
    dataIndex: 'tp',
    accessor: (row) => row.operation.tp,
    dataTestID: 'accounts-history-header-operation-tp',
    dataTestIDBody: 'accounts-history-body-operation-tp',
    Cell: renderWithoutZero,
  },
  {
    Header: 'Profit',
    dataIndex: 'profit',
    accessor: 'profit',
    align: 'right',
    dataTestID: 'accounts-history-header-profit',
    dataTestIDBody: 'accounts-history-body-profit',
    Cell: (cell) =>
      !cell.cell.row.original.operation.is_entry
        ? renderWithoutZeroAndRound(cell)
        : '',
  },
  {
    Header: 'Comment',
    dataIndex: 'comment',
    accessor: 'comment',
    align: 'left',
    dataTestID: 'accounts-history-header-comment',
    dataTestIDBody: 'accounts-history-body-comment',
  },
];

export const sortByTimeField = (isAsc) => (a, b) =>
  isAsc ? a.time - b.time : b.time - a.time;

export const valueWithoutZero = (value) => (String(value) === '0' ? '' : value);
